const waitForAuthReady = async (authStore: ReturnType<typeof useAuthStore>) => {
  if (authStore.isReady) {
    return;
  }

  await new Promise<void>((resolve) => {
    const timeoutId = window.setTimeout(() => {
      stopWatching();
      resolve();
    }, 5000);

    const stopWatching = watch(
      () => authStore.isReady,
      (isReady) => {
        if (!isReady) {
          return;
        }

        window.clearTimeout(timeoutId);
        stopWatching();
        resolve();
      },
      { immediate: true }
    );
  });
};

// Wait for Firebase to restore any persisted session before redirecting.
export default defineNuxtRouteMiddleware(async (to) => {
  if (!import.meta.client) {
    return;
  }

  const publicPaths = new Set([
    "/account-deletion",
  ]);
  const isAuthenticatePage = to.path.startsWith("/auth");
  const isPublicPage = publicPaths.has(to.path);
  const idToken = useCookie<string>("id_token", {
    sameSite: "lax",
    path: "/",
    secure: process.env.NODE_ENV === "production"
  });
  const userStore = useUserStore();
  const authStore = useAuthStore();

  if (idToken.value && !authStore.isReady) {
    await waitForAuthReady(authStore);
  }

  const isAuthenticated = Boolean(
    idToken.value || authStore.idToken || userStore.userDetails.userId
  );

  if (!isAuthenticated && !isAuthenticatePage && !isPublicPage) {
    userStore.changeUser({});
    idToken.value = "";
    const redirectTo = encodeURIComponent(to.fullPath);
    return navigateTo(`/auth/signin?redirectTo=${redirectTo}`);
  }

  if (isAuthenticated && to.path === "/auth/complete-activation") {
    return navigateTo("/numbers");
  }

  // Redirect authenticated users away from all other auth pages.
  // /auth/complete-activation is excluded above — it has its own redirect.
  if (isAuthenticated && isAuthenticatePage) {
    return navigateTo("/");
  }
});
